import {
  bulkContractualPartyShare,
  getContractualPartyShareByContractualParty,
  getContractualPartyShareByRole,
  getContractualPartyShareContractualParty,
  getContractualPartyShareUser,
  removeContractualPartyShare,
  removeContractualPartyShare2 as removeContractualPartyRoleShare,
  updateContractualPartySharePermissions,
  updateContractualPartySharePermissions2 as updateContractualPartyRoleSharePermissions,
  type ApplicationMessage,
  type ContractualPartyRoleShareInDb,
  type ContractualPartyUserShareInDb,
} from '@/api';
import { toAccessPermission } from '@/utils/accessPermission';
import { extractErrorDetail } from '@/utils/errorHandler';

/** Run a call, rethrowing its failure as an Error carrying the backend's detail. */
async function call<T>(request: () => Promise<{ data: T }>, fallback: string): Promise<T> {
  try {
    const { data } = await request();
    return data;
  } catch (error) {
    throw new Error(extractErrorDetail(error, fallback));
  }
}

/** Contractual-party shares, the same shape as `ContractShareService`. */
export const ContractualPartyShareService = {
  getUserSharesByParty(partyUid: string): Promise<ContractualPartyUserShareInDb[]> {
    return call(
      () =>
        getContractualPartyShareContractualParty({
          path: { contractual_party_uid: partyUid },
          throwOnError: true,
        }),
      'Failed to load shared users. Please try again.'
    );
  },

  getRoleSharesByParty(partyUid: string): Promise<ContractualPartyRoleShareInDb[]> {
    return call(
      () =>
        getContractualPartyShareByContractualParty({
          path: { contractual_party_uid: partyUid },
          throwOnError: true,
        }),
      'Failed to load shared roles. Please try again.'
    );
  },

  getPartySharesByUser(userUid: string): Promise<ContractualPartyUserShareInDb[]> {
    return call(
      () => getContractualPartyShareUser({ path: { user_uid: userUid }, throwOnError: true }),
      'Failed to load parties shared with this user. Please try again.'
    );
  },

  getPartySharesByRole(roleUid: string): Promise<ContractualPartyRoleShareInDb[]> {
    return call(
      () => getContractualPartyShareByRole({ path: { role_uid: roleUid }, throwOnError: true }),
      'Failed to load parties shared with this role. Please try again.'
    );
  },

  /** Share parties with users and/or roles; sharing a pair again overwrites its level. */
  bulkShareParties(params: {
    partyUids: string[];
    userUids?: string[];
    roleUids?: string[];
    permissions: number;
  }): Promise<ApplicationMessage> {
    return call(
      () =>
        bulkContractualPartyShare({
          body: {
            contractual_party_uids: params.partyUids,
            user_uids: params.userUids ?? [],
            role_uids: params.roleUids ?? [],
            permissions: toAccessPermission(params.permissions),
          },
          throwOnError: true,
        }),
      'Failed to share. Please try again.'
    );
  },

  updateUserSharePermissions(
    partyUid: string,
    userUid: string,
    permissions: number
  ): Promise<ContractualPartyUserShareInDb> {
    return call(
      () =>
        updateContractualPartySharePermissions({
          path: { contractual_party_uid: partyUid, user_uid: userUid },
          body: { permissions: toAccessPermission(permissions) },
          throwOnError: true,
        }),
      'Failed to update permissions. Please try again.'
    );
  },

  updateRoleSharePermissions(
    partyUid: string,
    roleUid: string,
    permissions: number
  ): Promise<ContractualPartyRoleShareInDb> {
    return call(
      () =>
        updateContractualPartyRoleSharePermissions({
          path: { contractual_party_uid: partyUid, role_uid: roleUid },
          body: { permissions: toAccessPermission(permissions) },
          throwOnError: true,
        }),
      'Failed to update permissions. Please try again.'
    );
  },

  removeUserShare(partyUid: string, userUid: string): Promise<ContractualPartyUserShareInDb> {
    return call(
      () =>
        removeContractualPartyShare({
          path: { contractual_party_uid: partyUid, user_uid: userUid },
          throwOnError: true,
        }),
      'Failed to remove user share. Please try again.'
    );
  },

  removeRoleShare(partyUid: string, roleUid: string): Promise<ContractualPartyRoleShareInDb> {
    return call(
      () =>
        removeContractualPartyRoleShare({
          path: { contractual_party_uid: partyUid, role_uid: roleUid },
          throwOnError: true,
        }),
      'Failed to remove role share. Please try again.'
    );
  },
};
